import { Injectable } from '@angular/core';
import {
  ChartFacade,
  TreatmentStepsFacade,
} from '@principle-theorem/ng-clinical-charting/store';
import { type SelectionListStore } from '@principle-theorem/ng-shared';
import {
  type IChartedMultiStepTreatment,
  type IChartedMultiStepTreatmentStep,
  type IChartedTreatmentSelectionSummary,
  type IGroupedTreatmentSelection,
  isChartedMultiStepTreatment,
  isChartedMultiStepTreatmentStep,
  isChartedTreatmentGroup,
  type ITreatmentPlan,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  filterUndefined,
  isSameRef,
  isWithRef,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { MoveChartable } from './add-chartable';
import { AddTreatmentToMultiTreatmentProposalProvider } from './charted-surface/chart/add-treatment-to-multi-treatment-proposal-provider';
import { AddTreatmentToProposalProvider } from './charted-surface/chart/add-treatment-to-proposal-provider';
import { AddTreatmentToStoreStepProvider } from './charted-surface/store-treatment-plan/add-treatment-to-store-step-provider';
import { type IEditTreatmentProvider } from './charted-surface/add-charted-surface-provider';

@Injectable()
export class TreatmentProviderService {
  constructor(
    private _chartFacade: ChartFacade,
    private _treatmentStepsFacade: TreatmentStepsFacade
  ) {}

  async getProvider(
    treatmentPlan?: IChartedMultiStepTreatment | WithRef<ITreatmentPlan>,
    treatmentStep?: IChartedMultiStepTreatmentStep | WithRef<ITreatmentStep>
  ): Promise<IEditTreatmentProvider> {
    const feeSchedule = await snapshot(
      this._chartFacade.currentSchedule$
    );

    if (isWithRef<ITreatmentStep>(treatmentStep)) {
      const step = await snapshot(
        this._treatmentStepsFacade
          .getTreatmentStep$(treatmentStep.ref.id)
          .pipe(filterUndefined())
      );
      return new AddTreatmentToStoreStepProvider(
        this._treatmentStepsFacade,
        step,
        feeSchedule
      );
    }

    if (
      isChartedMultiStepTreatment(treatmentPlan) &&
      isChartedMultiStepTreatmentStep(treatmentStep)
    ) {
      return new AddTreatmentToMultiTreatmentProposalProvider(
        this._chartFacade,
        treatmentPlan,
        treatmentStep,
        feeSchedule
      );
    }

    return new AddTreatmentToProposalProvider(this._chartFacade, feeSchedule);
  }

  async moveSelectedTreatments(
    selectionList: SelectionListStore<IChartedTreatmentSelectionSummary>,
    plan?: WithRef<ITreatmentPlan>,
    targetStepRef?: DocumentReference<ITreatmentStep>
  ): Promise<void> {
    const selected = await snapshot(selectionList.selected$);
    if (!selected.length) {
      return;
    }

    const groupedBySource = this._groupSelectionsBySource(selected);

    for (const group of groupedBySource) {
      if (isWithRef(group.step) && isSameRef(group.step.ref, targetStepRef)) {
        continue;
      }

      const targetStep = targetStepRef
        ? await snapshot(
            this._treatmentStepsFacade
              .getTreatmentStep$(targetStepRef.id)
              .pipe(filterUndefined())
          )
        : undefined;
      const addProvider = await this.getProvider(plan, targetStep);
      const removeProvider = await this.getProvider(group.plan, group.step);
      await new MoveChartable().move(
        group.treatments,
        removeProvider,
        addProvider
      );
    }

    selectionList.resetSelected();
  }

  private _groupSelectionsBySource(
    selected: IChartedTreatmentSelectionSummary[]
  ): IGroupedTreatmentSelection[] {
    const groups = new Map<string, IGroupedTreatmentSelection>();

    for (const { item, plan, step } of selected) {
      const key = isWithRef(step) ? step.ref.path : 'no-step';
      const treatments = isChartedTreatmentGroup(item)
        ? item.treatments
        : [item];
      const existing = groups.get(key);
      if (existing) {
        existing.treatments.push(...treatments);
      } else {
        groups.set(key, { plan, step, treatments: [...treatments] });
      }
    }

    return Array.from(groups.values());
  }
}
